import { useCallback, useMemo } from 'react'
import { TFunction } from 'react-i18next'
import { prismicText } from '@/components/Global/CMSText'
import { PrismicShop } from '../interfaces/common/prismicShop'

export type SortValue = Record<string, 'ASC' | 'DESC'> | null

export type SortingOption = {
  value: string
  label: string
}

export interface UseShopSortingReturn {
  defaultSortValue: SortValue
  enableSorting: boolean
  enableSortingByPriceAsc: boolean
  enableSortingByPriceDesc: boolean
  enableSortingByDateAsc: boolean
  enableSortingByDateDesc: boolean
  sortingOptions: SortingOption[]
  sortToString: (sort: SortValue) => string
  stringToSort: (value: string) => SortValue
}

export const useShopSorting = (
  shopData: PrismicShop | undefined,
  t?: TFunction
): UseShopSortingReturn => {
  const [
    enableSorting,
    enableSortingByPriceAsc,
    enableSortingByPriceDesc,
    enableSortingByDateAsc,
    enableSortingByDateDesc,
  ] = useMemo(
    () => [
      shopData?.data?.sort_by_price_asc ||
        shopData?.data?.sort_by_date_asc ||
        shopData?.data?.sort_by_price_desc ||
        shopData?.data?.sort_by_date_desc,
      shopData?.data?.sort_by_price_asc,
      shopData?.data?.sort_by_price_desc,
      shopData?.data?.sort_by_date_asc,
      shopData?.data?.sort_by_date_desc,
    ],
    [
      shopData?.data?.sort_by_price_asc,
      shopData?.data?.sort_by_date_asc,
      shopData?.data?.sort_by_price_desc,
      shopData?.data?.sort_by_date_desc,
    ]
  )

  const defaultSortValue = useMemo((): SortValue => {
    const defaultSorting = shopData?.data?.default_sorting_value
    if (!defaultSorting) return null

    // Map default_sorting_value to sort object format
    const sortMapping: Record<string, Record<string, 'ASC' | 'DESC'>> = {
      sort_by_price_asc: { price: 'ASC' as const },
      sort_by_price_desc: { price: 'DESC' as const },
      sort_by_date_asc: { date: 'ASC' as const },
      sort_by_date_desc: { date: 'DESC' as const },
    }

    const mappedSort = sortMapping[defaultSorting]
    if (!mappedSort) return null

    // Check if the corresponding enable flag is true
    const enableFlags = {
      sort_by_price_asc: enableSortingByPriceAsc,
      sort_by_price_desc: enableSortingByPriceDesc,
      sort_by_date_asc: enableSortingByDateAsc,
      sort_by_date_desc: enableSortingByDateDesc,
    }

    if (enableFlags[defaultSorting]) {
      return mappedSort
    }

    return null
  }, [
    shopData?.data?.default_sorting_value,
    enableSortingByPriceAsc,
    enableSortingByPriceDesc,
    enableSortingByDateAsc,
    enableSortingByDateDesc,
  ])

  const sortToString = useCallback((sort: SortValue): string => {
    if (!sort) return ''
    const key = Object.keys(sort)?.[0]
    return key ? `${key}:${sort[key]}` : ''
  }, [])

  const stringToSort = useCallback((value: string): SortValue => {
    if (!value) return null
    const [sortField, sortDirection] = value.split(':')
    if (!sortField || !sortDirection) return null
    return { [sortField]: sortDirection as 'ASC' | 'DESC' }
  }, [])

  const sortingOptions = useMemo((): SortingOption[] => {
    if (!t) return []

    const options: SortingOption[] = []

    if (enableSortingByPriceAsc) {
      options.push({
        value: 'price:ASC',
        label: prismicText(
          shopData?.data?.sort_by_price_asc_label,
          t('texts:shop:sort_by_price_asc')
        ),
      })
    }
    if (enableSortingByPriceDesc) {
      options.push({
        value: 'price:DESC',
        label: prismicText(
          shopData?.data?.sort_by_price_desc_label,
          t('texts:shop:sort_by_price_desc')
        ),
      })
    }
    if (enableSortingByDateDesc) {
      options.push({
        value: 'date:DESC',
        label: prismicText(
          shopData?.data?.sort_by_date_desc_label,
          t('texts:shop:sort_by_date_desc')
        ),
      })
    }
    if (enableSortingByDateAsc) {
      options.push({
        value: 'date:ASC',
        label: prismicText(
          shopData?.data?.sort_by_date_asc_label,
          t('texts:shop:sort_by_date_asc')
        ),
      })
    }

    return options
  }, [
    t,
    shopData?.data?.sort_by_price_asc_label,
    shopData?.data?.sort_by_price_desc_label,
    shopData?.data?.sort_by_date_asc_label,
    shopData?.data?.sort_by_date_desc_label,
    enableSortingByPriceAsc,
    enableSortingByPriceDesc,
    enableSortingByDateAsc,
    enableSortingByDateDesc,
  ])

  return {
    defaultSortValue,
    enableSorting,
    enableSortingByPriceAsc,
    enableSortingByPriceDesc,
    enableSortingByDateAsc,
    enableSortingByDateDesc,
    sortingOptions,
    sortToString,
    stringToSort,
  }
}
